import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, ViewChildren} from '@angular/core';
import {BaseService} from "../../services/base.service";
import {Meta, Title} from "@angular/platform-browser";
import {Subject} from "rxjs";
import {Delivery} from "../../interfaces/delivery";
import {Wars} from "../../interfaces/account-data";
import {BasketData, BasketGoods} from "../../interfaces/basket-data";
import {filter, finalize, take, takeUntil} from "rxjs/operators";
import * as moment from "moment";
import {HttpClient} from "@angular/common/http";
import {LocalStorageService} from "../../services/local-storage.service";
import {Router} from "@angular/router";
import {Clipboard} from "@angular/cdk/clipboard";
import {Currencies} from "../../interfaces/currencies";
import {ModalNoSelectedGoodsComponent} from "./modal-no-selected-goods/modal-no-selected-goods.component";
import {ModalUploadOrderComponent} from "./modal-upload-order/modal-upload-order.component";
import {OrganizationsService} from "../../services/organizations.service";
import {Organization} from "../../interfaces/organization";
import {removeDuplicateBrand} from "../../shared/brand-name";
import {AssistantActionsService} from "../../services/assistant-actions.service";
import {AssistantHighlight} from "../../shared/assistant-highlight";

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss']
})
export class BasketComponent extends BaseService implements OnDestroy, OnInit {
  @ViewChildren('inputCount') inputCountRefs;
  @ViewChildren('inputTaraCount') inputTaraCountRefs;
  @ViewChild(ModalNoSelectedGoodsComponent) modalNoSelectedGoodsComponentRef: ModalNoSelectedGoodsComponent;
  @ViewChild(ModalUploadOrderComponent) modalUploadOrderComponentRef: ModalUploadOrderComponent;

  public isDestroyed: Subject<boolean> = new Subject();

  private titleService = inject(Title);
  private meta = inject(Meta);
  private http = inject(HttpClient);
  private localStorage = inject(LocalStorageService);
  private router = inject(Router);
  private clipboard = inject(Clipboard);
  private organizationsService = inject(OrganizationsService);
  private assistantActions = inject(AssistantActionsService);

  // Картка #965: рядки, на які вказав помічник (додані чи змінені ним), і зняття його обробників
  highlight = new AssistantHighlight(inject(ElementRef));
  private assistantUnregister: Array<() => void> = [];
  // Скільки запитів basket_data/data ще без відповіді - прокрутка до підсвічених рядків чекає свіжий кошик
  private dataInFlight = 0;

  activeOrganization: Organization;
  // Валюта в цінах і підсумку (UAH/EUR) залежить від активної організації. Поки її не знаємо,
  // кошик не малюємо: інакше відповідь organizations/get_all перемальовувала вже показану сторінку (картка #388)
  isOrganizationKnown = false;

  delivery: number;
  deliveryList: Delivery[];
  deliveryCurrent: Delivery;
  wars: Wars[];
  isAdmin;
  allow_separate_orders;
  basketGoods: BasketGoods;
  sum_Convert_EUR_In_UAH: string;
  dateString;
  message;
  package_id: number;
  warehouse: number;
  turned_on: boolean = false;
  isButtonsOrdersShow: boolean = false;
  deliveryGoodDate: string;
  showMobileDelivery: boolean = false;
  currentYear: number;
  isCopyTextShow: boolean = false;
  copyText;
  // Таймер на кожен рядок (ключ - пакування+склад): зі спільним таймером швидка правка
  // двох рядків скасовувала відправку першого (аудити #300, #334)
  timersGoodCount: {[key: string]: any} = {};
  timersTaraCount: {[key: string]: any} = {};
  // Номер останнього запиту basket_data/data - відповіді старших запитів не застосовуємо
  private dataRequestSeq = 0;
  // Картка #515: локальна правка кількості, яку ще не зберіг бек. Перечитування кошика, що
  // почалося до неї або прийшло, поки вона чекає відправки, не застосовуємо - інакше воно
  // повертало старе число і наступний «+» рахувався від нього (клік губився).
  private countEditSeq = 0;
  // Правки кошика (кількість, видалення, очищення) йдуть на бек по черзі: відповідь на одну -
  // старт наступної. Так «+» не обганяє «+», а видалення не обганяє правку того ж рядка.
  private basketEditsChain: Promise<boolean> = Promise.resolve(true);
  private basketEditsInFlight = 0;
  private basketEditsFailed = 0;
  // Перечитування відкинули через незбережену правку - треба перечитати, коли правки скінчаться
  private refreshSkipped = false;
  // Відправка відкладеної (таймер 0,5 с) правки рядка «зараз» - для «Продовжити»
  private countFlushers: {[key: string]: () => void} = {};
  isContinuing = false;
  currencies: Currencies[];
  // Змінюється після кожного перечитування кошика - привід перерахувати місця (картка #218)
  estimateToken = 0;

  constructor() {
    super();

    const title = "Маслоточка | Кошик";
    this.currentYear = moment().year();
    this.titleService.setTitle(title);
    this.meta.addTag({name: 'title', content: title});
  }

  ngOnInit() {
    this.organizationsService.organizations$
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((organizations: Organization[]) => {
        if (!organizations || !organizations.length) {
          return;
        }

        this.activeOrganization = organizations.find((org) => org.is_active === '1' || org.is_active === true);
      });

    // loaded$ стає true вже після того, як organizations$ віддав список - activeOrganization на цей момент остаточна
    this.organizationsService.loaded$
      .pipe(filter(Boolean), take(1), takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.isOrganizationKnown = true;
      });

    this.getData();

    // Помічник змінив кошик: перечитати без перезавантаження сторінки (як після власної правки - не збиваючи
    // незбережене введення) і підсвітити його рядки
    this.assistantUnregister.push(
      this.assistantActions.register('refresh_basket', () => this.refreshAfterEdit()),
      this.assistantActions.register('highlight', (action) => {
        this.highlight.set(action.ids);

        if (!this.dataInFlight && !this.isBasketEditPending()) {
          this.highlight.ready();
        }
      }),
    );
  }

  getData() {
    const seq = ++this.dataRequestSeq;
    const editSeq = this.countEditSeq;

    this.dataInFlight++;

    return new Promise((resolve) => {
      this.delivery = parseInt(this.localStorage.get('delivery_id'));
      this.http.post(this.apiRoot + 'basket_data/data', {
        currency: this.localStorage.get('currencies'),
        delivery_id: this.localStorage.get('delivery_id')
      })
        // Помилка/обрив без відповіді - теж завершуємо проміс (false): інакше await getData() у
        // flushBasketEdits() висів, isContinuing лишався true і «Продовжити» більше не спрацьовувала.
        // Після resolve(true) цей виклик нічого не змінює.
        .pipe(takeUntil(this.isDestroyed), finalize(() => {
          this.dataInFlight--;
          resolve(false);

          // Останній запит завершився (чи впав, чи відповідь застаріла) і незбережених правок немає - прокрутити до
          // рядків помічника; інакше це зробить запит після останньої правки
          if (!this.dataInFlight && !this.isBasketEditPending()) {
            this.highlight.ready();
          }
        }))
        .subscribe((data: BasketData) => {
          // Поки йшла ця відповідь, кошик уже перечитано новішим запитом (правка іншого рядка)
          if (seq !== this.dataRequestSeq) {
            resolve(false);
            return;
          }

          // Після старту запиту змінили кількість або правка ще не дійшла до бека - відповідь
          // застаріла і затерла б введене. Перечитаємо після останньої правки.
          if (editSeq !== this.countEditSeq || this.isBasketEditPending()) {
            this.refreshSkipped = true;
            resolve(false);
            return;
          }

          this.refreshSkipped = false;

          // success:false - не обрана доставка (delivery_id видалено при зміні акаунта/виході) або виняток на беку.
          // delivery_info тоді немає, і Object.values(undefined) валив сторінку; лишаємо попередній стан.
          if (!data || !data.success) {
            resolve(false);
            return;
          }

          this.currencies = data.currencies ? Object.values(data.currencies) : [];
          this.deliveryList = Object.values(data.delivery_info).sort((a, b) => {
            const sortA = parseInt(a.sort);
            const sortB = parseInt(b.sort);

            if (sortA < sortB) {
              return -1;
            }
            if (sortA > sortB) {
              return 1;
            }

            return 0;
          });

          this.deliveryCurrent = this.deliveryList.find(element => element.id === this.delivery);

          this.wars = data.wars;
          this.basketGoods = data.basket_goods;

          this.isAdmin = data.is_admin;
          this.allow_separate_orders = data.allow_separate_orders;
          // Кошик/доставка змінились - кнопки «одним/різними» ховаємо: інакше вони лишались після зміни
          // доставки (де розділення заборонене) і обходили перевірки continueOrdering() (тара, нічого не обрано).
          // Бек allow_separate_orders при setup_session_separate_orders не перевіряє.
          this.isButtonsOrdersShow = false;

          if (this.basketGoods.goods_list) {
            this.basketGoods.goods_list = this.basketGoods.goods_list.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }

              return 0;
            });

            const turnedOnDates = this.basketGoods.goods_list.filter(data => data.turned_on)
              .map(date => date.departure_date);

            this.dateString = turnedOnDates.length ? Math.max(...turnedOnDates) : '';

            const currentDate = moment().format('DD.MM.YYYY');

            // this.dateString = moment(this.dateString.toString().toString().substr(0, 8)).format('DD.MM.YYYY'); //Выводит предупреждение после очистки корзины
            if (this.dateString) {
              this.dateString = moment(this.dateString.toString().substr(0, 8), 'YYYYMMDDHHmmss').format('DD.MM.YYYY');
            }

            if (!this.dateString) {
              this.deliveryGoodDate = '';
            } else if (this.dateString === 'Invalid date') {
              this.dateString = '';
            } else if (currentDate != this.dateString) {
              this.deliveryGoodDate = `Ваше замовлення буде доступно ${this.dateString}`;
            } else {
              this.deliveryGoodDate = '';
            }

            this.turned_on = false;
            for (let good of this.basketGoods.goods_list) {

              good.name = good.name.replaceAll('&nbsp;', ' ');
              // «MANN FILTER MANN-FILTER Фільтр мастил» -> «MANN-FILTER Фільтр мастил» (#855)
              good.name = removeDuplicateBrand(good.name, good.brand);

              if (good.turned_on) {
                this.turned_on = true;
              }

              if (good.article === null) {
                good.article = '';
              } else if (typeof good.article === "number") {
                good.first_article = good.article;
              } else {
                good.first_article = good.article.split('|||')[0];
              }
            }
          }

          const eurCurrency = this.currencies.find(el => el.id === 3);

          this.sum_Convert_EUR_In_UAH = eurCurrency
            ? (this.basketGoods.summa * eurCurrency.rate).toFixed(2)
            : (this.basketGoods.summa).toFixed(2);

          this.basketGoods.liters = (this.basketGoods.liters).toFixed(2);

          this.basketGoods.summa = (this.basketGoods.summa).toFixed(2);

          this.basketGoods.tara = Object.values(this.basketGoods.tara);

          this.basketGoods.unbottled_packs = Object.values(this.basketGoods.unbottled_packs);

          for (let unbottled of this.basketGoods.unbottled_packs) {
            const found = this.basketGoods.tara.find(el => el.package_id === unbottled.id);

            if (found) {
              unbottled.tara_priceEUR = found.priceEUR;
              unbottled.count_tara = found.count;
              unbottled.summa = (found.summa).toFixed(2);
              unbottled.warehouse = found.warehouse;
            } else {
              unbottled.count_tara = "0";
              unbottled.summa = "0";
              // Тари ще немає в кошику - ціну (EUR) бек дає готовою, тим самим PriceService, що й рядку кошика/замовлення (картка #448)
              unbottled.tara_priceEUR = String(unbottled.tara_price_eur);
              unbottled.warehouse = 1;
            }
          }

          this.basketGoods.liters = parseFloat(this.basketGoods.liters);

          // Склад кошика змінився - перерахувати кількість місць (картка #218)
          this.estimateToken++;

          resolve(true);
        });
    });
  }

  toggleAllBasketRows() {
    this.http.post(this.apiRoot + 'basket_data/toggle_all_basket_rows', {
      checked: !this.turned_on
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.getData();
      });
  }

  toggleBasketRow(good) {
    this.http.post(this.apiRoot + 'basket_data/toggle_basket_row', {
      package_id: good.package_id,
      warehouse: good.warehouse
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.getData();
      });
  };

  deleteFromBasket(good) {
    // Відкладена правка кількості цього рядка після видалення створила б його знову (#515)
    this.cancelCountTimer('g_' + good.package_id + '_' + good.warehouse);

    this.queueBasketEdit('basket_data/delete_from_basket', {
      pack_id: good.package_id,
      warehouse: good.warehouse
    }).then(() => this.refreshAfterEdit());
  };

  async continueOrdering() {
    if (this.isContinuing) {
      return;
    }

    // Спершу досилаємо відкладені правки кількості і перевіряємо вже збережений кошик (#515)
    if (this.isBasketEditPending()) {
      if (!await this.flushBasketEdits()) {
        return;
      }
    }

    const turnedOnGoods = this.basketGoods.goods_list.filter(good => good.turned_on);

    if (!turnedOnGoods.length) {
      this.modalNoSelectedGoodsComponentRef.openModal();
      return;
    }

    if (this.basketGoods.unbottled && this.basketGoods.unbottled_liters > this.basketGoods.unbottled_covered_liters) {
      alert('Необхідно вибрати і додати тару.');
    } else if (this.allow_separate_orders) {
      this.isButtonsOrdersShow = true;
    } else {
      this.setupSessionSeparateOrders(0);
    }
  }

  toggleFactoryPacking(good, event) {
    if (good.disallow_factory_packing) {
      event.preventDefault();

      alert('Можливість забрати в заводській упаковці буде доступно тільки після оформлення покупки залишків обраної позиції.');
    } else {
      this.http.post(this.apiRoot + 'basket_data/toggle_factory_packing', {
        pack_id: good.package_id,
        warehouse: good.warehouse
      })
        .pipe(takeUntil(this.isDestroyed))
        .subscribe(() => {
          this.getData();
        });
    }
  };

  notCopyText(event) {
    event.stopPropagation();
  }

  articleCopy(firstArticle) {
    this.clipboard.copy(firstArticle);

    this.isCopyTextShow = true;

    clearTimeout(this.copyText);
    this.copyText = setTimeout(() => {
      this.isCopyTextShow = false;
    }, 2000)
  }

  async setupSessionSeparateOrders(val) {
    if (this.isContinuing) {
      return;
    }

    // Після «Продовжити» кількість ще можна правити - теж досилаємо перед переходом (#515)
    if (this.isBasketEditPending()) {
      if (!await this.flushBasketEdits()) {
        return;
      }
    }

    this.http.post(this.apiRoot + '/basket_data/setup_session_separate_orders', {
      value: val
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(() => {
        this.router.navigate([`basket_confirm`]);
      });
  }

  setDelivery(id) {
    this.localStorage.set('delivery_id', id);

    this.delivery = id;

    this.deliveryCurrent = this.deliveryList.find(el => el.id === this.delivery);

    this.showMobileDelivery = false;

    this.getData()
  };

  showDelivery() {
    this.showMobileDelivery = !this.showMobileDelivery;
  }

  getOrders() {
    const delivery_id: string = this.localStorage.get('delivery_id');
    const currency: string = this.localStorage.get('currencies');
    window.open(this.apiRoot + `basket_data/download?currency=${currency}&delivery_id=${delivery_id}`, "_blank");
  };

  openUploadOrderModal() {
    this.modalUploadOrderComponentRef.openModal();
  };

  onOrderImported() {
    this.getData();
  };

  clearBasket() {
    // Відкладені правки кількості після очищення повернули б рядки в кошик (#515)
    Object.keys(this.countFlushers).forEach(key => this.cancelCountTimer(key));

    // Тільки POST: по GET-посиланню бек кошик більше не чистить (#400)
    this.queueBasketEdit('basket_data/clear_basket', {})
      .then(() => this.refreshAfterEdit());
  };

  minusCount(good) {
    good.count = parseInt(good.count) - 1

    if (good.count < 1) {
      good.count = 1;
    }

    this.countEditSeq++;

    this.queueBasketEdit('basket_data/edit_count_in_basket', {
      pack_id: good.package_id,
      value: good.count,
      warehouse: good.warehouse
    }).then(() => this.refreshAfterEdit());
  };

  goodInput(good, i) {
    const key = good.package_id + '_' + good.warehouse;
    clearTimeout(this.timersGoodCount[key]);
    this.countEditSeq++;

    const send = () => {
      clearTimeout(this.timersGoodCount[key]);
      delete this.timersGoodCount[key];
      delete this.countFlushers['g_' + key];

      // Каретку повертаємо, лише якщо користувач досі в цьому полі, а не перейшов до іншого рядка
      const input = this.inputCountRefs.get(i);
      const isFocused = !!input && document.activeElement === input.nativeElement;
      let anchorOffset = document.getSelection().anchorOffset;

      // Порожнє поле (стерли число, щоб набрати нове) або «0» (стерли першу цифру
      // з «10») бек читає як 0 і видаляє позицію з кошика. Видалення - лише
      // кнопкою-кошиком, тому не відправляємо.
      if (!(parseInt(good.count) >= 1)) {
        // Поки поле чекало, перечитування відкинули - якщо більше нічого не чекає, перечитуємо тут
        this.refreshIfSkipped();
        return;
      }

      if (good.count > 0) {
        good.count = parseInt(good.count);
      }

      this.queueBasketEdit('basket_data/edit_count_in_basket', {
        pack_id: good.package_id,
        value: good.count,
        warehouse: good.warehouse
      })
        .then(async () => {
          // Інший рядок ще чекає відправки - перечитаємо кошик після нього, щоб не збити введення
          if (!await this.refreshAfterEdit()) {
            return;
          }

          setTimeout(() => {
            if (isFocused && this.inputCountRefs.get(i)) {

              this.inputCountRefs.get(i).nativeElement.focus();

              const range = document.createRange();
              const sel = window.getSelection();

              if (this.inputCountRefs.get(i).nativeElement.childNodes[0].length < anchorOffset) {
                anchorOffset = this.inputCountRefs.get(i).nativeElement.childNodes[0].length;
              }

              range.setStart(this.inputCountRefs.get(i).nativeElement.childNodes[0], anchorOffset)
              range.collapse(true)

              sel.removeAllRanges()
              sel.addRange(range)
            }
          }, 0);
        });
    };

    this.timersGoodCount[key] = setTimeout(send, 500);
    this.countFlushers['g_' + key] = send;
  };


  plusCount(good) {
    good.count = parseInt(good.count) + 1

    if (isNaN(good.count)) {
      good.count = 1;
    }

    this.countEditSeq++;

    this.queueBasketEdit('basket_data/edit_count_in_basket', {
      pack_id: good.package_id,
      value: good.count,
      warehouse: good.warehouse
    }).then(() => this.refreshAfterEdit());
  };

  countMinusTara(unbottled) {
    unbottled.count_tara = parseInt(unbottled.count_tara) - 1;

    if (unbottled.count_tara < 1) {
      unbottled.count_tara = "0";
    }

    this.countEditSeq++;

    this.queueBasketEdit('basket_data/edit_count_in_basket', {
      pack_id: unbottled.id,
      value: unbottled.count_tara,
      warehouse: unbottled.warehouse
    }).then(() => this.refreshAfterEdit());
  }

  countGoodTara(unbottled, i) {
    const key = unbottled.id + '_' + unbottled.warehouse;
    clearTimeout(this.timersTaraCount[key]);
    this.countEditSeq++;

    const send = () => {
      clearTimeout(this.timersTaraCount[key]);
      delete this.timersTaraCount[key];
      delete this.countFlushers['t_' + key];

      const input = this.inputTaraCountRefs.get(i);
      const isFocused = !!input && document.activeElement === input.nativeElement;
      let anchorOffset = document.getSelection().anchorOffset;

      if (unbottled.count_tara > 0) {
        unbottled.count_tara = parseInt(unbottled.count_tara);
      } else if (!unbottled.count_tara) {
        unbottled.count_tara = "0";
      }

      this.queueBasketEdit('basket_data/edit_count_in_basket', {
        pack_id: unbottled.id,
        value: unbottled.count_tara,
        warehouse: unbottled.warehouse
      })
        .then(async () => {
          if (!await this.refreshAfterEdit()) {
            return;
          }

          setTimeout(() => {
            if (isFocused && this.inputTaraCountRefs.get(i)) {

              this.inputTaraCountRefs.get(i).nativeElement.focus();

              const range = document.createRange();
              const sel = window.getSelection();

              if (this.inputTaraCountRefs.get(i).nativeElement.childNodes[0].length < anchorOffset) {
                anchorOffset = this.inputTaraCountRefs.get(i).nativeElement.childNodes[0].length;
              }

              range.setStart(this.inputTaraCountRefs.get(i).nativeElement.childNodes[0], anchorOffset)
              range.collapse(true)

              sel.removeAllRanges()
              sel.addRange(range)
            }
          }, 0);
        });
    };

    this.timersTaraCount[key] = setTimeout(send, 500);
    this.countFlushers['t_' + key] = send;
  }

  countPlusTara(unbottled) {
    unbottled.count_tara = parseInt(unbottled.count_tara) + 1;

    if (isNaN(unbottled.count_tara)) {
      unbottled.count_tara = 1;
    }

    this.countEditSeq++;

    this.queueBasketEdit('basket_data/edit_count_in_basket', {
      pack_id: unbottled.id,
      value: unbottled.count_tara,
      warehouse: unbottled.warehouse
    }).then(() => this.refreshAfterEdit());
  }

  hasPendingCountTimers(): boolean {
    return Object.keys(this.timersGoodCount).length > 0 || Object.keys(this.timersTaraCount).length > 0;
  }

  isBasketEditPending(): boolean {
    return this.hasPendingCountTimers() || this.basketEditsInFlight > 0;
  }

  // Ставить правку кошика в чергу: запит піде, коли бек відповість на попередню. true - бек відповів.
  private queueBasketEdit(url: string, body: any): Promise<boolean> {
    this.basketEditsInFlight++;

    const request = this.basketEditsChain.then(() => new Promise<boolean>((resolve) => {
      this.http.post(this.apiRoot + url, body)
        .subscribe({
          next: () => resolve(true),
          // Помилку показує інтерсептор; обірваний запит (complete без відповіді) - теж «не збережено»
          error: () => resolve(false),
          complete: () => resolve(false)
        });
    }));

    this.basketEditsChain = request;

    return request.then((ok) => {
      this.basketEditsInFlight--;

      if (!ok) {
        this.basketEditsFailed++;
      }

      return ok;
    });
  }

  // Перечитує кошик, лише коли не лишилося незбережених правок - інакше це зробить остання з них
  private refreshAfterEdit(): Promise<unknown> {
    // Під час «Продовжити» кошик перечитує сам flushBasketEdits(). Другий getData() звідси робив його
    // запит застарілим (dataRequestSeq) - flush повертав false, і перший клік нічого не робив.
    // Якщо його перечитування не прийнялось, flush перечитає ще раз у finally.
    if (this.isContinuing) {
      this.refreshSkipped = true;
      return Promise.resolve(false);
    }

    if (this.isBasketEditPending()) {
      this.refreshSkipped = true;
      return Promise.resolve(false);
    }

    return this.getData();
  }

  private refreshIfSkipped() {
    if (this.refreshSkipped && !this.isBasketEditPending()) {
      this.getData();
    }
  }

  private cancelCountTimer(flusherKey: string) {
    if (!this.countFlushers[flusherKey]) {
      return;
    }

    const key = flusherKey.substring(2);
    const timers = flusherKey.startsWith('g_') ? this.timersGoodCount : this.timersTaraCount;

    clearTimeout(timers[key]);
    delete timers[key];
    delete this.countFlushers[flusherKey];
  }

  // Досилає відкладені правки, чекає всі відповіді й перечитує кошик. false - переходити далі не можна:
  // правка не збереглася (кількість у кошику не та, що бачив клієнт) або клієнт уже править далі.
  private async flushBasketEdits(): Promise<boolean> {
    this.isContinuing = true;

    try {
      const failedBefore = this.basketEditsFailed;

      Object.values(this.countFlushers).forEach(send => send());

      while (this.basketEditsInFlight > 0) {
        await this.basketEditsChain;
      }

      const refreshed = await this.getData();

      return refreshed === true && this.basketEditsFailed === failedBefore;
    } finally {
      this.isContinuing = false;
      this.refreshIfSkipped();
    }
  }

  ngOnDestroy() {
    this.assistantUnregister.forEach(unregister => unregister());
    this.highlight.destroy();
    Object.values(this.timersGoodCount).forEach(timer => clearTimeout(timer));
    Object.values(this.timersTaraCount).forEach(timer => clearTimeout(timer));
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  };

  protected readonly parseInt = parseInt;
}
